import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Scale, Filter, ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { ComparePost } from "@/content/compare/registry";
import {
  COMPARE_META as COMPARES,
  COMPARE_CATEGORY_LABELS as COMPARE_CATEGORIES,
} from "@/content/compare/meta.gen";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/compare/")({
  component: CompareHub,
  head: () => {
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: `เปรียบเทียบ Visa / Notary / Translation ${COMPARES.length}+ คู่ — Thai Notary`,
      description: `ตารางเทียบครบทุกมิติ: DTV vs LTR, NAATI vs MoJ, MFA vs Apostille, Thai vs International PCC ฯลฯ — TL;DR + ตารางคุณสมบัติ + FAQ ช่วยตัดสินใจ.`,
      path: "/compare",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/compare")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "เปรียบเทียบ", url: "/compare" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Compare / Versus Pages",
            itemListElement: COMPARES.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/compare/${c.slug}`,
              name: c.title,
            })),
          }),
        },
      ],
    };
  },
});

type CategoryKey = ComparePost["category"] | "all";

function CompareHub() {
  const [cat, setCat] = useState<CategoryKey>("all");
  const filtered = useMemo(
    () => (cat === "all" ? COMPARES : COMPARES.filter((c) => c.category === cat)),
    [cat],
  );
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold">
              <Scale className="h-3.5 w-3.5" /> Compare · {COMPARES.length} versus
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">
              เปรียบเทียบ Visa / Notary / Translation / Marriage
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              ตัดสินใจไม่ได้ระหว่าง 2 ตัวเลือก? — เราเปรียบเทียบครบทุกมิติ ราคา, เวลา, สิทธิ,
              ภาษี, eligibility และ scenarios ใครเหมาะกับอะไร พร้อม TL;DR สั้นให้ AI Search
              ดึงคำตอบและ FAQ ครบทุกคำถามที่ลูกค้าถามจริง.
            </p>
          </div>
        </section>

        <section className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3">
            <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Filter className="h-3.5 w-3.5" /> หมวด
            </span>
            {(["all", ...Object.keys(COMPARE_CATEGORIES)] as CategoryKey[]).map((c) => {
              const label =
                c === "all"
                  ? `ทั้งหมด (${COMPARES.length})`
                  : COMPARE_CATEGORIES[c as ComparePost["category"]];
              const count =
                c === "all"
                  ? COMPARES.length
                  : COMPARES.filter((x) => x.category === c).length;
              if (c !== "all" && count === 0) return null;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    cat === c
                      ? "border-gold bg-gold text-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-gold"
                  }`}
                >
                  {c === "all" ? label : `${label} (${count})`}
                </button>
              );
            })}
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-6xl gap-4 px-4 py-10 md:grid-cols-2">
            {filtered.map((c) => (
              <Link
                key={c.slug}
                to="/compare/$slug"
                params={{ slug: c.slug }}
                className="group flex flex-col rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-gold hover:shadow-lg"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold">
                  {COMPARE_CATEGORIES[c.category]}
                </div>
                <h2 className="mt-1 text-lg font-extrabold text-foreground group-hover:text-primary">
                  {c.title}
                </h2>
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="rounded-md bg-primary/5 px-2 py-0.5 font-semibold text-primary">A</span>
                  <span className="line-clamp-1">{c.aName}</span>
                  <span className="text-muted-foreground/50">vs</span>
                  <span className="rounded-md bg-gold/10 px-2 py-0.5 font-semibold text-gold">B</span>
                  <span className="line-clamp-1">{c.bName}</span>
                </div>
                <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{c.tldr}</p>
                <div className="mt-auto pt-4 inline-flex items-center gap-1 text-xs font-bold text-primary">
                  อ่านเปรียบเทียบครบ <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
